<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Addon Packages</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Manage pre-configured addon packages</p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openModal(null)">
        Add Package
      </v-btn>
    </div>

    <!-- Packages Table -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="packages"
        :loading="loading"
        item-key="id"
        density="comfortable"
      >
        <template #item.most_popular="{ item }">
          <v-chip v-if="item.most_popular" size="small" color="success">Yes</v-chip>
          <span v-else class="text-medium-emphasis">No</span>
        </template>
        <template #item.price_per_person="{ item }">
          ${{ Number(item.price_per_person).toFixed(2) }}
        </template>
        <template #item.addons="{ item }">
          <div class="d-flex flex-wrap" style="gap: 4px;">
            <v-chip
              v-for="addon in item.addons"
              :key="addon.id"
              size="small"
              variant="tonal"
              color="primary"
            >
              {{ addon.name }}
            </v-chip>
            <span v-if="!item.addons || !item.addons.length" class="text-medium-emphasis">None</span>
          </div>
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex justify-center" style="gap: 4px;">
            <v-btn icon variant="text" size="small" @click="openModal(item)">
              <v-icon size="20">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon variant="text" size="small" color="error" @click="confirmDelete(item)">
              <v-icon size="20">mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Modal -->
    <v-dialog v-model="isModalOpen" max-width="700px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">{{ selectedPackage ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ selectedPackage ? 'Edit Package' : 'Add Package' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Package Name"
            v-model="form.name"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            required
          ></v-text-field>
          <v-textarea
            label="Description"
            v-model="form.description"
            variant="outlined"
            density="comfortable"
            rows="3"
            class="mb-3"
          ></v-textarea>
          <v-text-field
            label="Price Per Person"
            v-model.number="form.price_per_person"
            type="number"
            prefix="$"
            variant="outlined"
            density="comfortable"
            class="mb-3"
          ></v-text-field>
          <v-checkbox
            v-model="form.most_popular"
            label="Most Popular"
            color="primary"
            hide-details
            density="compact"
            class="mb-4"
          ></v-checkbox>

          <h3 class="text-subtitle-1 font-weight-bold mb-3">Assign Addon Items</h3>
          <v-autocomplete
            v-model="form.addonItemIds"
            :items="allAddonItems"
            item-title="displayName"
            item-value="id"
            label="Select addon items"
            variant="outlined"
            density="comfortable"
            multiple
            chips
            closable-chips
            clearable
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="isModalOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="savePackage">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="420px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="text-body-1">
          Are you sure you want to delete this addon package? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deletePackage">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { addonPackageService, addonService } from "@/services";
import { useNotificationStore } from "@/stores";

defineOptions({ name: "AddonPackagesView" });

const notificationStore = useNotificationStore();

const packages = ref([]);
const allAddonItems = ref([]);
const loading = ref(false);
const isModalOpen = ref(false);
const selectedPackage = ref(null);
const showDeleteConfirm = ref(false);
const packageToDelete = ref(null);

const form = reactive({
  name: "",
  description: "",
  price_per_person: 0,
  most_popular: false,
  addonItemIds: [],
});

const headers = [
  { title: "ID", key: "id", width: "70px" },
  { title: "Name", key: "name" },
  { title: "Description", key: "description" },
  { title: "Price/Person", key: "price_per_person", width: "120px" },
  { title: "Most Popular", key: "most_popular", width: "120px" },
  { title: "Addon Items", key: "addons" },
  { title: "Actions", key: "actions", sortable: false, width: "120px" },
];

async function fetchPackages() {
  loading.value = true;
  try {
    const response = await addonPackageService.getAddonPackages();
    packages.value = response.data || [];
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to load addon packages");
  } finally {
    loading.value = false;
  }
}

async function fetchAllAddonItems() {
  try {
    const response = await addonService.getAddons();
    const categories = response.data || [];
    const items = [];
    for (const cat of categories) {
      for (const item of cat.items || []) {
        items.push({
          id: item.id,
          displayName: `${item.name} ($${Number(item.price).toFixed(2)})`,
        });
      }
    }
    allAddonItems.value = items;
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to load addon items");
  }
}

function openModal(pkg) {
  selectedPackage.value = pkg;
  if (pkg) {
    Object.assign(form, {
      name: pkg.name,
      description: pkg.description,
      price_per_person: Number(pkg.price_per_person),
      most_popular: pkg.most_popular,
      addonItemIds: (pkg.addons || []).map((a) => a.id),
    });
  } else {
    Object.assign(form, {
      name: "",
      description: "",
      price_per_person: 0,
      most_popular: false,
      addonItemIds: [],
    });
  }
  isModalOpen.value = true;
}

async function savePackage() {
  try {
    const payload = {
      name: form.name,
      description: form.description,
      price_per_person: form.price_per_person,
      most_popular: form.most_popular,
      addonItemIds: form.addonItemIds,
    };
    if (selectedPackage.value) {
      await addonPackageService.updateAddonPackage(selectedPackage.value.id, payload);
      notificationStore.showSuccess("Package updated successfully");
    } else {
      await addonPackageService.createAddonPackage(payload);
      notificationStore.showSuccess("Package created successfully");
    }
    isModalOpen.value = false;
    fetchPackages();
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to save package");
  }
}

function confirmDelete(pkg) {
  packageToDelete.value = pkg;
  showDeleteConfirm.value = true;
}

async function deletePackage() {
  try {
    await addonPackageService.deleteAddonPackage(packageToDelete.value.id);
    showDeleteConfirm.value = false;
    notificationStore.showSuccess("Package deleted successfully");
    fetchPackages();
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to delete package");
  }
}

onMounted(() => {
  fetchPackages();
  fetchAllAddonItems();
});
</script>
